/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import type { ColumnDef } from '@tanstack/react-table'

/** Pixel widths (Tailwind spacing scale: unit × 4px at default 16px root). */
export const TABLE_COLUMN_WIDTH_PX = {
    id: 80,
    seller: 128,
    name: 176,
    contact: 256,
    company: 192,
    companyType: 144,
    dateRange: 208,
    timeRange: 224,
    persons: 112,
    status: 176,
    tags: 160,
    standard: 128,
    count: 96,
    offers: 164,
    package: 160,
    stay: 112,
    revenue: 128,
    email: 256,
    phone: 192,
    position: 144,
    website: 160,
    guid: 192,
    content: 256,
    reminderType: 160,
    actions: 112,
    actionsCompact: 64,
    icon: 40,
} as const

export type TableColumnWidthKey = keyof typeof TABLE_COLUMN_WIDTH_PX

/** Minimum column widths; table stretches to 100% and columns grow beyond these when space allows. */
export const TABLE_COLUMN_WIDTH: Record<TableColumnWidthKey, string> = {
    id: 'min-w-20 shrink-0',
    seller: 'min-w-32 shrink-0',
    name: 'min-w-44 shrink-0',
    contact: 'min-w-64 shrink-0',
    company: 'min-w-48 shrink-0',
    companyType: 'min-w-36 shrink-0',
    dateRange: 'min-w-52 shrink-0',
    timeRange: 'min-w-56 shrink-0',
    persons: 'min-w-28 shrink-0',
    status: 'min-w-44 shrink-0',
    tags: 'min-w-40 shrink-0',
    standard: 'min-w-32 shrink-0',
    count: 'min-w-24 shrink-0',
    offers: 'min-w-[164px] shrink-0',
    package: 'min-w-40 shrink-0',
    stay: 'min-w-28 shrink-0',
    revenue: 'min-w-64 shrink-0',
    email: 'min-w-64 shrink-0',
    phone: 'min-w-48 shrink-0',
    position: 'min-w-36 shrink-0',
    website: 'min-w-40 shrink-0',
    guid: 'min-w-48 shrink-0',
    content: 'min-w-64 shrink-0',
    reminderType: 'min-w-40 shrink-0',
    actions: 'min-w-28 shrink-0',
    actionsCompact: 'min-w-16 shrink-0',
    icon: 'min-w-10 shrink-0',
}

export type TableColumnExportField = {
    key: string
    labelKey: string
}

export type TableColumnMeta = {
    /** Override width token when column id is ambiguous. */
    widthKey?: TableColumnWidthKey
    /** Row action button count — sets actions column min width (px-2 icon buttons + gap-2). */
    actionCount?: number
    /** Backend export field(s) represented by this table column. */
    exportFields?: TableColumnExportField[]
    /** @deprecated Use widthKey / column id map — ignored when widthKey or id map matches. */
    className?: string
}

/**
 * Maps TanStack column id → shared width token.
 * Same id = same width in every table (Leads, Opportunities, Offers, …).
 */
export const TABLE_COLUMN_ID_TO_WIDTH_KEY: Record<string, TableColumnWidthKey> = {
    id: 'id',
    select: 'icon',
    seller_id: 'seller',
    name: 'name',
    contact: 'contact',
    company: 'company',
    company_name: 'company',
    company_id: 'company',
    company_type: 'companyType',
    'company.type': 'companyType',
    type: 'companyType',
    reminder_type: 'reminderType',
    category: 'status',
    employee_size: 'standard',
    status: 'status',
    lifecycle_status: 'status',
    tags: 'tags',
    created_at: 'dateRange',
    created_date: 'dateRange',
    closed_at: 'dateRange',
    updated_at: 'dateRange',
    valid_until: 'dateRange',
    requested_date: 'dateRange',
    requested_time: 'timeRange',
    date: 'dateRange',
    due_at: 'dateRange',
    next_action_date: 'dateRange',
    source: 'standard',
    origin: 'standard',
    company_branche: 'status',
    business_category: 'status',
    company_state: 'standard',
    region: 'standard',
    company_address: 'standard',
    address: 'company',
    contact_position: 'position',
    position: 'position',
    contact_gender: 'standard',
    gender: 'standard',
    title: 'standard',
    persons: 'persons',
    persons_reserved: 'persons',
    persons_effective: 'persons',
    potential_value: 'revenue',
    value: 'revenue',
    total: 'revenue',
    total_invoice: 'revenue',
    probability: 'standard',
    offers: 'offers',
    guid: 'guid',
    offer_number: 'name',
    package: 'package',
    event_time: 'timeRange',
    time: 'timeRange',
    has_reservation: 'standard',
    calculations_count: 'count',
    duration_breakdown: 'stay',
    email: 'email',
    phone: 'phone',
    leads: 'count',
    reservations: 'count',
    postal_code: 'standard',
    town: 'standard',
    street_name: 'standard',
    website: 'website',
    content: 'content',
    entity: 'standard',
    reminders: 'icon',
    actions: 'actions',
}

/** Matches `UiTableRowActionButton` (px-2 + w-4 icon + border) and `gap-2` between buttons. */
export const TABLE_ACTION_BUTTON_WIDTH_PX = 36
export const TABLE_ACTION_BUTTON_GAP_PX = 8
export const TABLE_ACTIONS_CELL_PADDING_X_PX = 24
export const TABLE_ACTIONS_HEADER_MIN_PX = 36

export const getActionsColumnWidthPx = (actionCount: number): number => {
    if (actionCount <= 0) {
        return TABLE_ACTIONS_HEADER_MIN_PX
    }

    const buttonsWidth =
        actionCount * TABLE_ACTION_BUTTON_WIDTH_PX + Math.max(0, actionCount - 1) * TABLE_ACTION_BUTTON_GAP_PX

    return Math.max(TABLE_ACTIONS_HEADER_MIN_PX, TABLE_ACTIONS_CELL_PADDING_X_PX + buttonsWidth)
}

export const resolveTableColumnWidthKey = (
    columnId: string,
    meta?: TableColumnMeta
): TableColumnWidthKey | undefined => {
    if (meta?.widthKey) {
        return meta.widthKey
    }

    return TABLE_COLUMN_ID_TO_WIDTH_KEY[columnId]
}

export const getTableColumnWidthPx = (meta: TableColumnMeta | undefined, columnId: string): number | undefined => {
    if (columnId === 'actions') {
        return getActionsColumnWidthPx(meta?.actionCount ?? 2)
    }

    const widthKey = resolveTableColumnWidthKey(columnId, meta)
    return widthKey ? TABLE_COLUMN_WIDTH_PX[widthKey] : undefined
}

export const getTableColumnWidthStyle = (
    meta: TableColumnMeta | undefined,
    columnId: string
): { minWidth: number } | undefined => {
    const px = getTableColumnWidthPx(meta, columnId)
    if (!px) {
        return undefined
    }

    return { minWidth: px }
}

export const getTableColumnClassName = (meta: TableColumnMeta | undefined, columnId: string): string => {
    if (columnId === 'actions') {
        return 'shrink-0'
    }

    const widthKey = resolveTableColumnWidthKey(columnId, meta)
    if (widthKey) {
        return TABLE_COLUMN_WIDTH[widthKey]
    }

    return meta?.className ?? ''
}

export const sumTableColumnWidthsPx = (columnIds: Array<{ id: string; meta?: TableColumnMeta }>): number =>
    columnIds.reduce((total, column) => {
        return total + (getTableColumnWidthPx(column.meta, column.id) ?? 0)
    }, 0)

/** Column meta for actions — width scales with button count. */
export const tableActionsMeta = (actionCount: number) => ({
    meta: { actionCount } satisfies TableColumnMeta,
})

/** Optional explicit meta for columns that need a non-default width token. */
export const tableColumnMeta = (widthKey: TableColumnWidthKey) => ({
    meta: { widthKey } satisfies TableColumnMeta,
})

const resolveColumnDefId = (column: ColumnDef<unknown, unknown>): string | undefined => {
    if (column.id) {
        return column.id
    }

    const accessorKey = (column as { accessorKey?: string | number }).accessorKey
    if (typeof accessorKey === 'string') {
        return accessorKey
    }

    return undefined
}

/** Applies TanStack column size/minSize/maxSize from the shared width registry. */
export const applyTableColumnWidths = <T>(columns: ColumnDef<T, unknown>[]): ColumnDef<T, unknown>[] =>
    columns.map((column) => {
        const nestedColumns = (column as ColumnDef<T, unknown> & { columns?: ColumnDef<T, unknown>[] }).columns

        if (nestedColumns?.length) {
            return {
                ...column,
                columns: applyTableColumnWidths(nestedColumns),
            }
        }

        const columnId = resolveColumnDefId(column as ColumnDef<unknown, unknown>)
        if (!columnId) {
            return column
        }

        const px = getTableColumnWidthPx(column.meta as TableColumnMeta | undefined, columnId)
        if (!px) {
            return column
        }

        if (columnId === 'actions') {
            return {
                ...column,
                size: px,
                minSize: px,
            }
        }

        return {
            ...column,
            size: px,
            minSize: px,
            maxSize: px,
        }
    })
